import { INIT_EVENTS } from "../../constants";
import { shouldWriteRemote, upsertEventApp, upsertEventsApp } from "../../services/supabaseEntityWrites";
import { logger, toLogError } from "../../utils/logger";

/**
 * Événements calendrier + récurrence — écritures Supabase si coach distant.
 */
export function createEventsSlice(set, get) {
  return {
    events: INIT_EVENTS,

    setEvents: (fn) => set((s) => ({ events: typeof fn === "function" ? fn(s.events) : fn })),

    createEvent: (eventData) => {
      const prev = get().events;
      const id = crypto.randomUUID();
      set((s) => ({
        events: [
          ...s.events,
          {
            id,
            status: "scheduled",
            coachId: get().coachId,
            workoutId: null,
            ...eventData,
          },
        ],
      }));

      void (async () => {
        if (!shouldWriteRemote(get().coachId)) {
          get().persist?.();
          return;
        }
        const coachId = get().coachId;
        const row = get().events.find((e) => e.id === id);
        const r = await upsertEventApp(row, coachId);
        if (!r.ok) {
          set({ events: prev });
          logger.error("[createEvent] remote write failed", toLogError(r.error), { eventId: id });
        }
      })();
    },

    createRecurringSeries: (baseEvent, weekCount) => {
      const prev = get().events;
      const safeWeekCount = Math.min(weekCount, 104);
      const groupId = crypto.randomUUID();
      const newEvents = [];
      for (let i = 0; i < safeWeekCount; i++) {
        const d = new Date(baseEvent.date + "T12:00:00");
        d.setDate(d.getDate() + i * 7);
        const y = d.getFullYear();
        const m = String(d.getMonth() + 1).padStart(2, "0");
        const dd = String(d.getDate()).padStart(2, "0");
        const dateISO = `${y}-${m}-${dd}`;
        newEvents.push({
          ...baseEvent,
          id: crypto.randomUUID(),
          date: dateISO,
          recurringGroupId: groupId,
          status: "scheduled",
          sessionTitle: baseEvent.sessionTitle ?? null,
          coachId: get().coachId,
          workoutId: baseEvent.workoutId ?? null,
        });
      }
      set((s) => ({ events: [...s.events, ...newEvents] }));

      void (async () => {
        if (!shouldWriteRemote(get().coachId)) {
          get().persist?.();
          return;
        }
        const r = await upsertEventsApp(newEvents, get().coachId);
        if (!r.ok) {
          set({ events: prev });
          logger.error("[createRecurringSeries] remote write failed", toLogError(r.error));
        }
      })();
    },

    extendRecurringSeries: () => {
      const events = get().events;
      const groups = [
        ...new Set(
          events.filter((e) => e.recurringGroupId && e.status === "scheduled").map((e) => e.recurringGroupId)
        ),
      ];

      const newEvents = [];
      const HORIZON_WEEKS = 12;
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const todayISO = today.toISOString().slice(0, 10);
      const horizonDate = new Date(today);
      horizonDate.setDate(horizonDate.getDate() + HORIZON_WEEKS * 7);

      for (const groupId of groups) {
        if (newEvents.length >= 100) break;
        const groupEvents = events.filter((e) => e.recurringGroupId === groupId && e.status === "scheduled");
        if (groupEvents.length === 0) continue;

        const lastDate = groupEvents.reduce((max, e) => (e.date > max ? e.date : max), "");
        const hasStoppedSeriesAfterLast = events.some(
          (e) =>
            e.recurringGroupId === groupId &&
            (e.status === "cancelled" || e.status === "standby") &&
            e.date > lastDate
        );
        if (hasStoppedSeriesAfterLast) continue;

        const lastDateObj = new Date(lastDate + "T12:00:00");

        if (lastDateObj < horizonDate) {
          const template = groupEvents[0];
          let nextDate = new Date(lastDateObj);

          while (nextDate < horizonDate) {
            nextDate.setDate(nextDate.getDate() + 7);
            const y = nextDate.getFullYear();
            const m = String(nextDate.getMonth() + 1).padStart(2, "0");
            const d = String(nextDate.getDate()).padStart(2, "0");
            const iso = `${y}-${m}-${d}`;

            if (
              iso >= todayISO &&
              !events.some((e) => e.recurringGroupId === groupId && e.date === iso) &&
              !newEvents.some((e) => e.recurringGroupId === groupId && e.date === iso)
            ) {
              if (newEvents.length >= 100) {
                logger.warn("[AthleOS] extendRecurringSeries: garde activée, 100 events max par appel");
                break;
              }
              newEvents.push({
                id: crypto.randomUUID(),
                date: iso,
                time: template.time,
                duration: template.duration,
                clientId: template.clientId,
                client: template.client,
                type: "recurring",
                recurringGroupId: groupId,
                tarif: template.tarif,
                status: "scheduled",
                billable: false,
                sessionTitle: null,
                coachId: get().coachId,
                workoutId: template.workoutId ?? null,
              });
            }
          }
        }
      }

      if (newEvents.length === 0) return;

      const prev = get().events;
      set((s) => ({ events: [...s.events, ...newEvents] }));

      void (async () => {
        if (!shouldWriteRemote(get().coachId)) {
          get().persist?.();
          return;
        }
        const r = await upsertEventsApp(newEvents, get().coachId);
        if (!r.ok) {
          set({ events: prev });
          logger.error("[extendRecurringSeries] remote write failed", toLogError(r.error));
        }
      })();
    },

    updateEvent: (eventId, changes) => {
      const prev = get().events;
      set((s) => ({
        events: s.events.map((e) => (e.id === eventId ? { ...e, ...changes } : e)),
      }));

      void (async () => {
        if (!shouldWriteRemote(get().coachId)) {
          get().persist?.();
          return;
        }
        const e = get().events.find((x) => x.id === eventId);
        const r = await upsertEventApp(e, get().coachId);
        if (!r.ok) {
          set({ events: prev });
          logger.error("[updateEvent] remote write failed", toLogError(r.error));
        }
      })();
    },

    updateRecurringSeries: (recurringGroupId, fromDate, changes) => {
      const prev = get().events;
      set((s) => {
        let dayDelta = 0;
        if (changes.date) {
          const oldD = new Date(fromDate + "T12:00:00");
          const newD = new Date(changes.date + "T12:00:00");
          dayDelta = Math.round((newD - oldD) / (1000 * 60 * 60 * 24));
        }
        return {
          events: s.events.map((e) => {
            if (e.recurringGroupId !== recurringGroupId) return e;
            if (e.date < fromDate) return e;
            if (e.status === "cancelled" || e.status === "standby") return e;

            const updated = { ...e };
            if (dayDelta !== 0) {
              const d = new Date(e.date + "T12:00:00");
              d.setDate(d.getDate() + dayDelta);
              const y = d.getFullYear();
              const m = String(d.getMonth() + 1).padStart(2, "0");
              const dd = String(d.getDate()).padStart(2, "0");
              updated.date = `${y}-${m}-${dd}`;
            }
            if (changes.time !== undefined) updated.time = changes.time;
            if (changes.duration !== undefined) updated.duration = changes.duration;
            if (changes.workoutId !== undefined) updated.workoutId = changes.workoutId;
            if (changes.clientId !== undefined) {
              updated.clientId = changes.clientId;
              updated.client = changes.client;
            }
            return updated;
          }),
        };
      });

      void (async () => {
        if (!shouldWriteRemote(get().coachId)) {
          get().persist?.();
          return;
        }
        const toSave = get().events.filter(
          (e) => e.recurringGroupId === recurringGroupId && e.date >= fromDate && e.status === "scheduled"
        );
        const r = await upsertEventsApp(toSave, get().coachId);
        if (!r.ok) {
          set({ events: prev });
          logger.error("[updateRecurringSeries] remote write failed", toLogError(r.error));
        }
      })();
    },

    convertToRecurring: (eventId, weekCount = 12) => {
      const events = get().events;
      const original = events.find((e) => e.id === eventId);
      if (!original) return;

      const prev = get().events;
      const groupId = crypto.randomUUID();
      const newEvents = [];

      for (let i = 1; i < weekCount; i++) {
        const d = new Date(original.date + "T12:00:00");
        d.setDate(d.getDate() + i * 7);
        const y = d.getFullYear();
        const m = String(d.getMonth() + 1).padStart(2, "0");
        const dd = String(d.getDate()).padStart(2, "0");
        newEvents.push({
          id: crypto.randomUUID(),
          date: `${y}-${m}-${dd}`,
          time: original.time,
          duration: original.duration,
          clientId: original.clientId,
          client: original.client,
          type: "recurring",
          recurringGroupId: groupId,
          tarif: original.tarif,
          status: "scheduled",
          billable: false,
          sessionTitle: null,
          coachId: get().coachId,
          workoutId: original.workoutId ?? null,
        });
      }

      set((s) => ({
        events: s.events
          .map((e) => (e.id === eventId ? { ...e, type: "recurring", recurringGroupId: groupId } : e))
          .concat(newEvents),
      }));

      void (async () => {
        if (!shouldWriteRemote(get().coachId)) {
          get().persist?.();
          return;
        }
        const coachId = get().coachId;
        const updatedOrig = get().events.find((e) => e.id === eventId);
        const r = await upsertEventsApp([updatedOrig, ...newEvents], coachId);
        if (!r.ok) {
          set({ events: prev });
          logger.error("[convertToRecurring] remote write failed", toLogError(r.error));
        }
      })();
    },
  };
}
